import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLogin() {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(credentials)
      });
      
      if (!response.ok) throw new Error('Invalid credentials');
      
      const data = await response.json();
      // Store session token based on remember me preference
      if (rememberMe) {
        localStorage.setItem('session_token', data.session_token);
      } else {
        sessionStorage.setItem('session_token', data.session_token);
      }
      
      // Store admin role and permissions (always in localStorage for consistency)
      if (data.admin_role) {
        localStorage.setItem('admin_role', data.admin_role);
      }
      if (data.permissions) {
        localStorage.setItem('admin_permissions', JSON.stringify(data.permissions));
      }
      
      // Also store in sessionStorage for fallback
      if (!rememberMe && data.session_token) {
        sessionStorage.setItem('admin_role', data.admin_role || 'admin');
        sessionStorage.setItem('admin_permissions', JSON.stringify(data.permissions || {}));
      }
      
      toast.success(`Login successful! (${data.admin_role || 'admin'})`);
      navigate('/admin/dashboard', { state: { user: data.user } });
    } catch (error) {
      toast.error('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card>
          <CardContent className="pt-6">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Admin Login
              </h1>
              <p className="text-muted-foreground mt-2">KILO BEATERS Partner Portal</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="admin-email-input"
                  type="email"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="admin-password-input"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="admin-remember-me" 
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  data-testid="admin-remember-me-checkbox"
                />
                <label 
                  htmlFor="admin-remember-me" 
                  className="text-sm text-muted-foreground cursor-pointer select-none"
                >
                  Remember me on this device
                </label>
              </div>
              
              <Button
                data-testid="admin-login-button"
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
              
              <div className="text-center pt-4 border-t mt-4">
                <button
                  type="button"
                  className="text-sm text-orange-600 hover:text-orange-800 underline"
                  onClick={() => {
                    if (!confirm('Reset all admin passwords to defaults?\n\nThis will set:\n• shyma@kilobeaters.com → Merman123\n• admin@kilobeaters.com → Admin123\n• viewer@kilobeaters.com → Viewer123\n\nContinue?')) return;
                    
                    const xhr = new XMLHttpRequest();
                    xhr.open('POST', `${API}/auth/admin/setup-admins`, true);
                    xhr.onload = function() {
                      if (xhr.status >= 200 && xhr.status < 300) {
                        toast.success('Admin passwords reset!');
                        alert('Passwords Reset!\n\nSuper Admin: shyma@kilobeaters.com / Merman123\nAdmin: admin@kilobeaters.com / Admin123\nViewer: viewer@kilobeaters.com / Viewer123');
                      } else {
                        toast.error('Failed to reset passwords');
                        alert('Error: ' + xhr.responseText);
                      }
                    };
                    xhr.onerror = function() {
                      toast.error('Network error');
                    };
                    xhr.send();
                  }}
                >
                  Reset Passwords
                </button>
                <span className="mx-2 text-gray-400">|</span>
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                  onClick={() => {
                    const xhr = new XMLHttpRequest();
                    xhr.open('GET', `${API}/auth/admin/debug-check`, true);
                    xhr.onload = function() {
                      if (xhr.status >= 200 && xhr.status < 300) {
                        const data = JSON.parse(xhr.responseText);
                        alert('Database Check:\n\n' + 
                          `Database: ${data.database}\n` +
                          `Admins found: ${data.admins_found}\n\n` +
                          'Admin emails:\n' + 
                          (data.admins.map(a => `• ${a.email} (${a.admin_role})`).join('\n') || 'None'));
                      } else {
                        alert('Error: ' + xhr.responseText);
                      }
                    };
                    xhr.onerror = function() {
                      alert('Network error');
                    };
                    xhr.send();
                  }}
                >
                  Check DB
                </button>
                <span className="mx-2 text-gray-400">|</span>
                <button
                  type="button"
                  className="text-sm text-green-600 hover:text-green-800 underline"
                  onClick={() => {
                    const email = document.querySelector('#email')?.value || '';
                    const password = document.querySelector('#password')?.value || '';
                    
                    if (!email || !password) {
                      alert('Please enter email and password first');
                      return;
                    }
                    
                    const xhr = new XMLHttpRequest();
                    xhr.open('POST', `${API}/auth/admin/debug-login`, true);
                    xhr.setRequestHeader('Content-Type', 'application/json');
                    xhr.onload = function() {
                      const data = JSON.parse(xhr.responseText);
                      alert('Debug Login Result:\n\n' + JSON.stringify(data, null, 2));
                    };
                    xhr.onerror = function() {
                      alert('Network error');
                    };
                    xhr.send(JSON.stringify({ email, password }));
                  }}
                >
                  Test Login
                </button>
              </div>
            </form>

            {/* Admin credentials stored securely in database */}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}